import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client used by every island.
 *
 * When this module runs inside the legacy host (`index.html`), the legacy
 * page already created a client via the CDN UMD bundle and exposed it as
 * `window.__sbClient`. We reuse it so there's exactly one auth-token
 * refresh timer per origin — two clients on the same origin sometimes
 * collide on token refresh and emit `refresh_token_already_used` errors.
 *
 * When this module runs standalone (tests, a future React-only deploy),
 * `window.__sbClient` is undefined and we fall back to building a fresh
 * client from the `VITE_SUPABASE_*` env vars.
 *
 * **Lazy resolution via Proxy**: the client is built on first property
 * access, not at module load. This lets the legacy host's `const sb = ...`
 * (which sets `window.__sbClient`) run between bundle load and first
 * island mount — without the laziness, the React module evaluates first
 * and creates a duplicate standalone client. See plan R3.
 */

let cached: SupabaseClient | null = null

function getClient(): SupabaseClient {
  if (cached) return cached
  const bridged = (globalThis as { __sbClient?: SupabaseClient }).__sbClient
  if (bridged) {
    cached = bridged
    return bridged
  }

  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !anonKey) {
    throw new Error(
      'Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env',
    )
  }
  cached = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  })
  return cached
}

export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getClient()
    const value = Reflect.get(client, prop, client)
    return typeof value === 'function' ? value.bind(client) : value
  },
})

/** Test-only helper. Resets the cached client so a different mock can take over. */
export function _resetSupabaseClientForTests(): void {
  cached = null
}
